import { AxiosError } from 'axios';
import { get, includes, isArray, isFunction } from 'lodash';
import { put } from 'redux-saga/effects';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import { EntityBase } from '../resources/types';

import { PayloadApiRequest } from './sagaApi';
import showMessage from './showMessage';
import updateLoading from './updateLoading';

export interface MetaHandleApiError {
  callbackError?: (response?: AxiosError) => void;
  camelizeResponseKeys?: boolean;
  ignoreErrors?: boolean | number[];
  messageError?: string | ((error: AxiosError) => string);
  skipMessages?: boolean;
}

export default function* sagaHandleApiError<E extends EntityBase>(error: AxiosError, payload: PayloadApiRequest<E>) {
  const { action, meta = {} } = payload;
  const errorCtx = { errorStack: error, context: { action, meta } };

  if (error.response) {
    const errorCode = get(error, 'response.status', 'no status code');
    const errorData = get(
      error,
      'response.data.message',
      get(error, 'response.data', get(error, 'response.statusText', 'no error message')),
    );
    if (errorCode === 401 || errorCode === 403) {
      sendLog(`API ERROR code: ${errorCode} data: ${errorData}`, errorCtx, LoggerStatusEnum.info);
    } else {
      sendLog(`API ERROR code: ${errorCode} data: ${errorData}`, errorCtx, LoggerStatusEnum.error);
    }
  } else {
    sendLog('API ERROR no response object found', errorCtx, LoggerStatusEnum.warn);
  }
  // updateLoading false
  if (meta.showLoading) {
    yield put(updateLoading(false));
  }

  const ignoreError =
    (isArray(meta.ignoreErrors) && includes(meta.ignoreErrors, get(error, 'response.status'))) || meta.ignoreErrors;
  if (!ignoreError) {
    // Dispatch `failure` action
    if (action) {
      yield put(action.failure({ error, meta }));
    }
    // Show message error
    if (!meta.skipMessages) {
      let messageError;
      if (meta.messageError) {
        messageError = isFunction(meta.messageError) ? meta.messageError(error) : meta.messageError;
      } else {
        const genericErrorMessage = 'Unknown server error';
        messageError =
          get(error, 'response.data.statusText') || get(error, 'response.statusText') || genericErrorMessage;
        if (messageError === genericErrorMessage) {
          sendLog("Error on sagaHandleApiError doesn't have a valid error message", error, LoggerStatusEnum.info);
        }
      }
      yield put(showMessage(messageError, 'error'));
    }
  }
  // Callback error
  if (meta.callbackError) {
    meta.callbackError(error);
  }
  return error;
}
